// U4 placeholder gate, in the card: a draft that marks facts Claude couldn't know as
// `[[fill: the date]]` lists them under the editor ("Fill in before sending: the date · the
// amount"); each one, pressed, selects that spot in the draft so typing replaces it. Sending stays
// blocked until none is left (here and on the server).
import type { RefObject } from "react";
import { findPlaceholders, type Placeholder } from "@shared/placeholders";

/** Selects a placeholder in the card's editor (the first one unless `at` is given). */
export function focusPlaceholder(
  editor: HTMLTextAreaElement | null,
  at?: Pick<Placeholder, "start" | "end">,
): boolean {
  if (!editor) return false;
  const target = at ?? findPlaceholders(editor.value)[0];
  if (!target) return false;
  editor.focus();
  editor.setSelectionRange(target.start, target.end);
  return true;
}

/** The card's draft editor (by the `data-card-editor` attribute the editable block sets). */
export function cardEditor(cardId: string, blockId?: string): HTMLTextAreaElement | null {
  const sel = `[data-card-editor="${CSS.escape(cardId)}"]`;
  const all = [...document.querySelectorAll<HTMLTextAreaElement>(sel)];
  return (blockId ? all.find((el) => el.dataset.blockId === blockId) : undefined) ?? all[0] ?? null;
}

export function PlaceholderPrompt({
  value,
  editorRef,
}: {
  value: string;
  editorRef: RefObject<HTMLTextAreaElement | null>;
}) {
  const found = findPlaceholders(value);
  if (!found.length) return null;
  const firsts = found.filter((p, i) => found.findIndex((q) => q.label === p.label) === i);
  return (
    <div
      data-testid="placeholder-prompt"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-note-line bg-note px-3 py-2 text-sm text-note-ink"
    >
      <span>Fill in before sending:</span>
      {firsts.map((p, i) => (
        <span key={`${p.label}:${p.start}`} className="inline-flex items-center gap-2">
          {i > 0 ? <span aria-hidden>·</span> : null}
          <button
            type="button"
            className="font-medium underline underline-offset-2"
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => focusPlaceholder(editorRef.current, p)}
          >
            {p.label}
          </button>
        </span>
      ))}
    </div>
  );
}
